import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  List, 
  IconButton,
  useTheme,
  Tooltip,
  Paper,
  useMediaQuery
} from '@mui/material';
import { Icon } from '@mui/material';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';
import { useSnackbar } from 'notistack';
import notesService from 'src/services/notes.service';
import { Note } from 'src/features/notes/notes.slice';
import NoteForm from '../Notes/NoteForm';
import GenerateActivityButton from './GenerateActivityButton';
import CustomMarkdown from '../CustomMarkdown';
import ContactCallDialog from './ContactCallDialog';
// import { useAppSelector } from 'src/store'; // Reserved for future use
import ConversationPreviewTooltip from '../Channels/ConversationPreviewTooltip';

// Utility function to check if content is likely HTML
const isHtmlContent = (text: string): boolean => {
  if (!text) return false;
  // Simple check for HTML tags
  const htmlTagPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlTagPattern.test(text);
};

// Simple function to sanitize HTML strings
const sanitizeHtml = (html: string): string => {
  // Basic sanitization - remove script tags and on* attributes
  let sanitized = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/g, '')
    .replace(/on\w+='[^']*'/g, '')
    .replace(/javascript:/gi, '')
    .replace(/onerror=/gi, '')
    .replace(/onclick=/gi, '')
    .replace(/onload=/gi, '');
    
  // Make iframes safe by adding sandbox attribute
  sanitized = sanitized.replace(
    /<iframe(.+?)>/gi, 
    '<iframe$1 sandbox="allow-scripts allow-same-origin" style="max-width:100%; border:0;">'
  );
  
  // Ensure images don't break layout
  sanitized = sanitized.replace(
    /<img(.+?)>/gi, 
    '<img$1 style="max-width:100%; height:auto;">'
  );
  
  return sanitized;
};

// Safe HTML renderer component
const SafeHtmlContent = ({ html }: { html: string }) => {
  // Sanitize the HTML content
  const sanitizedHtml = sanitizeHtml(html);
  
  return (
    <Box 
      sx={{
        '& img': { maxWidth: '100%', height: 'auto' },
        '& iframe': { maxWidth: '100%', border: 0 },
        '& table': { maxWidth: '100%', fontSize: '0.875rem' },
        '& *': { maxWidth: '100%' },
        overflow: 'hidden',
        wordBreak: 'break-word',
        fontSize: 'inherit',
        lineHeight: 1.6
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    </Box>
  );
};

// Content renderer that handles both Markdown and HTML
const ContentRenderer = React.memo(function ContentRenderer({ content }: { content: string }) {
  const [renderFallback, setRenderFallback] = useState(false);
  const theme = useTheme();
  
  // Memoize heavy rendering logic (runs every render but only recomputes when content changes)
  const renderedContent = useMemo(() => {
  if (!content) return null;
  
    try {
      if (isHtmlContent(content)) {
        return <SafeHtmlContent html={content} />;
      }
      return <CustomMarkdown>{content}</CustomMarkdown>;
    } catch (error) {
      console.error('Error rendering content:', error);
      setRenderFallback(true);
      return null;
    }
  }, [content]);

  if (renderFallback) {
    return (
      <Box 
        sx={{ 
          p: 2, 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.03)',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This content contains complex formatting that couldn't be rendered safely.
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {content}
        </Typography>
      </Box>
    );
  }
  
  return renderedContent;
});

interface TimelineEntryProps {
  type: 'note' | 'conversation' | 'call';
  item: any;
  timestamp: number;
  onClick?: () => void;
  onDelete?: (id: string) => void;
  onUpdate?: (id: string, data: any) => void;
  // Icon-specific hover handlers (used only for conversation type)
  onIconMouseEnter?: (e: React.MouseEvent) => void;
  onIconMouseLeave?: (e: React.MouseEvent) => void;
}

// Timeline Entry Component - Displays individual entries
const TimelineEntryComponent: React.FC<TimelineEntryProps> = function TimelineEntryComponent({ type, item, timestamp, onClick, onDelete, onUpdate, onIconMouseEnter, onIconMouseLeave }) {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const [isEditing, setIsEditing] = useState(false);
  
  // Get appropriate icon based on entry type
  const getIcon = () => {
    switch (type) {
      case 'conversation':
        return <Box
          className="conversation-icon"
          onMouseEnter={onIconMouseEnter}
          onMouseLeave={onIconMouseLeave}
          sx={{
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            background: item.status === 'closed' 
              ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, #1ab88f 100%)`
              : `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, #5a5ad8 100%)`,
            border: '1px solid',
            borderColor: item.status === 'closed' 
              ? 'rgba(33, 209, 159, 0.2)' 
              : 'rgba(73, 73, 200, 0.2)',
            boxShadow: item.status === 'closed'
              ? '0 2px 8px rgba(33, 209, 159, 0.2)'
              : '0 2px 8px rgba(73, 73, 200, 0.2)',
            color: '#fff',
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {item.status === 'closed' ? <Icon sx={{ fontSize: 18 }}>chat_bubble_outline</Icon> : <Icon sx={{ fontSize: 18 }}>chat</Icon>}
        </Box>
      case 'call':
        return <Box
          sx={{
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, #1ab88f 100%)`,
            border: '1px solid rgba(33, 209, 159, 0.2)',
            boxShadow: '0 2px 8px rgba(33, 209, 159, 0.2)',
            color: '#fff',
            flexShrink: 0
          }}
        >
          <Icon sx={{ fontSize: 18 }}>phone</Icon>
        </Box>
      case 'note':
        return <Box
          sx={{
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            background: darkMode
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
              : theme.palette.background.glass,
            border: '1px solid',
            borderColor: darkMode
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(73, 73, 200, 0.1)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            color: theme.palette.text.primary,
            flexShrink: 0
          }}
        >
          <Icon sx={{ fontSize: 18 }}>description</Icon>
        </Box>
      default:
        return <Box
          sx={{
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            background: darkMode
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
              : theme.palette.background.glass,
            border: '1px solid',
            borderColor: darkMode
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(73, 73, 200, 0.1)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            color: theme.palette.text.primary,
            flexShrink: 0
          }}
        >
          <Icon sx={{ fontSize: 18 }}>chat</Icon>
        </Box>
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };
  
  const handleUpdate = async (updatedData: any) => {
    if (onUpdate) {
      await onUpdate(item.id, updatedData);
    }
    setIsEditing(false);
  };
  
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(item.id);
    }
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // Render edit form for notes if editing mode is enabled
  if (isEditing && type === 'note') {
    return (
      <Box sx={{ mb: 2 }}>
        <NoteForm
          note={item}
          onSave={handleUpdate}
          onCancel={handleCancelEdit}
        />
      </Box>
    );
  }

  // Main entry rendering
  return (
    <Box 
      sx={{ 
        display: 'flex',
        gap: 1.5,
        p: 2,
        borderRadius: '12px',
        background: darkMode 
          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)'
          : theme.palette.background.glass,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid',
        borderColor: darkMode 
          ? 'rgba(255, 255, 255, 0.05)' 
          : 'rgba(73, 73, 200, 0.05)',
        boxShadow: darkMode
          ? '0 2px 8px rgba(0, 0, 0, 0.2)'
          : '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          background: darkMode 
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%)'
            : theme.palette.background.glassStrong,
          borderColor: darkMode 
            ? 'rgba(255, 255, 255, 0.1)' 
            : 'rgba(73, 73, 200, 0.1)',
          boxShadow: darkMode
            ? '0 4px 16px rgba(0, 0, 0, 0.3)'
            : '0 4px 16px rgba(73, 73, 200, 0.08)',
          transform: 'translateY(-2px)',
          '& .action-buttons': {
            opacity: 1,
            visibility: 'visible'
          }
        },
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default'
      }}
      onClick={onClick}
    >
      {/* Icon */}
      {getIcon()}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 0.5 
        }}>
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                fontFamily: theme.typography.h6.fontFamily,
                letterSpacing: '-0.01em',
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              {item.subject || (
                type === 'conversation'
                  ? 'Conversation'
                  : type === 'note'
                  ? 'Internal Note'
                  : 'Call'
              )}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {item.sender_name || 'System'}
              {item.created_at && (
                <>
                  <Box 
                    component="span" 
                    sx={{ 
                      mx: 0.5,
                      color: theme.palette.text.disabled 
                    }}
                  >
                    •
                  </Box>
                  {dateTimeFormatter(timestamp, 'day_month_ordinal_humanized_time_period')}
                </>
              )}
            </Typography>
          </Box>
        </Box>

        <Box 
          sx={{ 
            color: theme.palette.text.primary,
            mt: 0.5,
            overflow: 'hidden',
            maxWidth: '100%',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            '& p': {
              margin: 0,
              marginBottom: 0.5,
              '&:last-child': {
                marginBottom: 0,
              }
            }
          }}
        >
          <ContentRenderer content={item.content || item.body} />
        </Box>
      </Box>

      {/* Action buttons */}
      {(type === 'note' && onDelete && onUpdate) && (
        <Box 
          className="action-buttons"
          sx={{ 
            position: 'absolute',
            top: '12px',
            right: '12px',
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            display: 'flex',
            gap: 0.5,
            zIndex: 1
          }}
        >
          <IconButton 
            size="small" 
            onClick={handleEdit}
            sx={{
              width: 32,
              height: 32,
              padding: 0,
              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              border: '1px solid',
              borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(73, 73, 200, 0.1)',
                borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(73, 73, 200, 0.2)'
              }
            }}
          >
            <Icon fontSize="small">edit</Icon>
          </IconButton>
          <IconButton 
            size="small" 
            onClick={handleDelete}
            sx={{
              width: 32,
              height: 32,
              padding: 0,
              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              border: '1px solid',
              borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              '&:hover': {
                backgroundColor: 'rgba(255, 59, 59, 0.1)',
                borderColor: 'rgba(255, 59, 59, 0.2)',
                color: theme.palette.error.main
              }
            }}
          >
            <Icon fontSize="small">delete</Icon>
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

// Memoized TimelineEntry to avoid unnecessary re-renders
const TimelineEntry = React.memo(TimelineEntryComponent, (prev, next) => {
  // Prevent re-render if the item hasn't changed and callbacks are equal
  return (
    prev.type === next.type &&
    prev.item?.id === next.item?.id &&
    prev.item?.updated_at === next.item?.updated_at &&
    prev.onClick === next.onClick &&
    prev.onDelete === next.onDelete &&
    prev.onUpdate === next.onUpdate
  );
});

// Timeline Container Component - Arranges entries in a column
const TimelineContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      my: 1
    }}>
      {children}
    </Box>
  );
};

// Empty State Component - Displayed when no entries exist
const EmptyTimelineState: React.FC<{ onAddNote: () => void, onNewMessage: () => void }> = ({ 
  onAddNote, 
  onNewMessage 
}) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      p: 4,
      minHeight: '200px',
      background: theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)'
        : theme.palette.background.glass,
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderRadius: '16px',
      border: '1px solid',
      borderColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(73, 73, 200, 0.05)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 16px rgba(0, 0, 0, 0.2)'
        : '0 4px 16px rgba(0, 0, 0, 0.04)'
    }}>
      <Icon sx={{ 
        fontSize: 48, 
        color: 'text.secondary', 
        mb: 2 
      }}>
        history
      </Icon>
      <Typography 
        variant="body1" 
        color="text.secondary" 
        align="center"
        sx={{ mb: 3 }}
      >
        No activity found for this contact
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        gap: 2 
      }}>
        <Button
          variant="outlined"
          startIcon={<Icon>note_add</Icon>}
          onClick={onAddNote}
          sx={{
            height: '48px',
            borderRadius: '12px',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(249, 249, 255, 0.9) 0%, rgba(240, 240, 255, 0.8) 100%)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: theme => theme.palette.text.primary,
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(73, 73, 200, 0.1)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.3)'
              : '0 2px 8px rgba(73, 73, 200, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)'
                : 'linear-gradient(135deg, rgba(249, 249, 255, 1) 0%, rgba(235, 235, 255, 0.9) 100%)',
              borderColor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.2)' 
                : 'rgba(73, 73, 200, 0.2)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 16px rgba(0, 0, 0, 0.4)'
                : '0 4px 16px rgba(73, 73, 200, 0.15)',
              transform: 'translateY(-1px)'
            },
            '&:active': {
              transform: 'translateY(0)'
            }
          }}
        >
          Add Note
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon>message</Icon>}
          onClick={onNewMessage}
          sx={{
            height: '48px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, #5a5ad8 100%)`,
            color: '#fff',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 12px rgba(73, 73, 200, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            fontWeight: 500,
            letterSpacing: '0.02em',
            '&:hover': {
              background: `linear-gradient(135deg, #5a5ad8 0%, ${theme.palette.secondary.main} 100%)`,
              boxShadow: '0 6px 20px rgba(73, 73, 200, 0.4)',
              transform: 'translateY(-1px)',
              borderColor: 'rgba(255, 255, 255, 0.2)'
            },
            '&:active': {
              transform: 'translateY(0)'
            }
          }}
        >
          New Message
        </Button>
      </Box>
    </Box>
  );
};

// Main Timeline Component interface
interface ContactTimelineProps {
  contactId: string;
  conversations?: any[];
  onConversationClick?: (conversationId: string, channelId: string) => void;
  onAddNote?: () => void;
  onNewMessage?: () => void;
  onNewCall?: () => void;
}

// Main ContactTimeline Component
const ContactTimeline: React.FC<ContactTimelineProps> = ({ 
  contactId, 
  conversations,
  onConversationClick,
  onAddNote,
  onNewMessage
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timelineEntries, setTimelineEntries] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  // const { user } = useAppSelector(state => state.user); // Reserved for future use
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  
  // Tooltip state - simplified
  const [tooltipConversationId, setTooltipConversationId] = useState<string | undefined>(undefined);
  const [tooltipAnchorEl, setTooltipAnchorEl] = useState<HTMLElement | null>(null);
  const tooltipTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Ref to track whether the component is still mounted
  const isMountedRef = React.useRef(true);

  // Set isMountedRef.current to false on unmount to prevent state updates
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Clear any pending tooltip timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);
  
  // Simple tooltip handlers without complex dependencies
  const handleConversationMouseEnter = useCallback((e: React.MouseEvent, conversation: any) => {
    // Clear any pending hide timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    
    if (conversation && conversation.id && !isMobile) {
      const target = e.currentTarget as HTMLElement;
      setTooltipConversationId(conversation.id);
      setTooltipAnchorEl(target);
    }
  }, [isMobile]);
  
  const handleConversationMouseLeave = useCallback(() => {
    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    // Set a new timeout
    tooltipTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setTooltipConversationId(undefined);
        setTooltipAnchorEl(null);
      }
      tooltipTimeoutRef.current = null;
    }, 200);
  }, []);
  
  const handleCloseTooltip = useCallback(() => {
    // Clear any pending timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setTooltipConversationId(undefined);
    setTooltipAnchorEl(null);
  }, []);

  // Fetch notes for the contact
  const fetchNotes = async () => {
    try {
      const params = { owner_id: contactId, owner_type: 'Contact' };
      const response = await notesService.getNotes(params);
      if (isMountedRef.current) {
        setNotes(response.data.notes || []);
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
      if (isMountedRef.current) {
        setError('Error fetching notes');
      }
    }
  };
  
  // Load data when component mounts or contactId changes
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!cancelled && isMountedRef.current) {
        setLoading(true);
      }
      await fetchNotes();
      if (!cancelled && isMountedRef.current) {
        setLoading(false);
      }
    };
    
    if (contactId) {
      fetchData();
    }

    return () => {
      cancelled = true;
    };
  }, [contactId]);
  
  // Stable default conversations array
  const stableConversations = useMemo(() => conversations || [], [conversations]);
  
  // Combine and sort notes and conversations
  useEffect(() => {
    const noteEntries = notes.map(note => ({
      type: 'note',
      timestamp: new Date(note.created_at).getTime(),
      data: note
    }));
    
    const conversationEntries = stableConversations.map(conversation => ({
      type: 'conversation',
      timestamp: new Date(conversation.latest_delivery_at || conversation.updated_at).getTime(),
      data: conversation
    }));
    
    // Sort all entries by timestamp, newest first
    const combined = [...noteEntries, ...conversationEntries]
      .sort((a, b) => b.timestamp - a.timestamp);
      
    setTimelineEntries(combined);
  }, [notes, stableConversations]);
  
  // Handle updating an existing note
  const handleUpdateNote = async (noteId: string, updatedData: any) => {
    try {
      const response = await notesService.updateNote(noteId, updatedData);
      setNotes(prevNotes => prevNotes.map(note => 
        note.id === noteId ? response.data.note : note
      ));
    } catch (err) {
      console.error('Error updating note:', err);
      enqueueSnackbar('Error updating note', { variant: 'error' });
    }
  };
  
  // Handle deleting a note
  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }
    
    try {
      await notesService.deleteNote(noteId);
      setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      enqueueSnackbar('Error deleting note', { variant: 'error' });
    }
  };

  // Handle conversation click
  const handleConversationClick = (conversationId: string, channelId: string) => {
    if (onConversationClick) {
      onConversationClick(conversationId, channelId);
    }
  };

  // Handle opening the call dialog
  const handleOpenCallDialog = () => {
    setCallDialogOpen(true);
  };

  // Handle closing the call dialog
  const handleCloseCallDialog = () => {
    setCallDialogOpen(false);
  };

  // Handle showing the note form
  const handleAddNoteClick = () => {
    setShowNoteForm(true);
  };

  // Handle canceling the note form
  const handleCancelNote = () => {
    setShowNoteForm(false);
  };

  // Handle saving a new note
  const handleSaveNote = async (noteData: any) => {
    setIsSubmittingNote(true);
    try {
      // Ensure the note has the contact ID and type
      const noteWithOwner = {
        ...noteData,
        owner_id: contactId,
        owner_type: 'Contact'
      };
      
      const response = await notesService.createNote(noteWithOwner);
      setNotes(prevNotes => [response.data.note, ...prevNotes]);
      setShowNoteForm(false);
      enqueueSnackbar('Note created successfully', { variant: 'success' });
      return response;
    } catch (err) {
      console.error('Error creating note:', err);
      enqueueSnackbar('Error creating note', { variant: 'error' });
      throw err;
    } finally {
      setIsSubmittingNote(false);
    }
  };

  return (
    <Box>
      {/* Activity Header with Action Buttons */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mb: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between'
        }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 600,
            fontFamily: theme.typography.h6.fontFamily,
            letterSpacing: '-0.01em',
            color: theme => theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '1rem'
          }}>
            <Box sx={{
              width: 3,
              height: 20,
              borderRadius: 1.5,
              background: `linear-gradient(180deg, ${theme.palette.secondary.main} 0%, #5a5ad8 100%)`,
              mr: 0.25
            }} />
            Activity
          </Typography>

          {/* AI Activity Summary Button */}
          <GenerateActivityButton 
            contactId={contactId} 
            onSuccess={fetchNotes}
            onError={() => enqueueSnackbar('Error generating activity note', { variant: 'error' })}
          />
        </Box>

        <Box sx={{
          display: 'flex',
          gap: 1
        }}>
          <Button
            variant="outlined"
            startIcon={<Icon sx={{ fontSize: 18 }}>note_add</Icon>}
            onClick={handleAddNoteClick}
            sx={{
              flex: 1,
              height: '36px',
              borderRadius: '8px',
              fontSize: '0.813rem',
              px: 1.5,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              '& .MuiButton-startIcon': {
                marginRight: 0.5,
              },
              background: darkMode 
                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
                : theme.palette.background.glass,
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: theme => theme.palette.text.primary,
              border: '1px solid',
              borderColor: darkMode 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(73, 73, 200, 0.1)',
              boxShadow: darkMode
                ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                : '0 2px 8px rgba(73, 73, 200, 0.08)',
              textTransform: 'none',
              fontWeight: 500,
              fontFamily: theme.typography.button.fontFamily,
              letterSpacing: '0.01em',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: darkMode 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)'
                  : theme.palette.background.glassStrong,
                borderColor: darkMode 
                  ? 'rgba(255, 255, 255, 0.2)' 
                  : 'rgba(73, 73, 200, 0.2)',
                boxShadow: darkMode
                  ? '0 4px 16px rgba(0, 0, 0, 0.4)'
                  : '0 4px 16px rgba(73, 73, 200, 0.15)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Add Note
            </Box>
          </Button>

          <Button
            variant="contained"
            startIcon={<Icon sx={{ fontSize: 18 }}>message</Icon>}
            onClick={onNewMessage}
            sx={{
              flex: 1,
              height: '36px',
              borderRadius: '8px',
              fontSize: '0.813rem',
              px: 1.5,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              '& .MuiButton-startIcon': {
                marginRight: 0.5,
              },
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, #5a5ad8 100%)`,
              color: '#fff',
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              boxShadow: '0 4px 12px rgba(73, 73, 200, 0.3)',
              textTransform: 'none',
              fontWeight: 500,
              fontFamily: theme.typography.button.fontFamily,
              letterSpacing: '0.02em',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: `linear-gradient(135deg, #5a5ad8 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: '0 6px 20px rgba(73, 73, 200, 0.4)',
                transform: 'translateY(-1px)',
                borderColor: 'rgba(255, 255, 255, 0.2)'
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              New Message
            </Box>
          </Button>

          <Button
            variant="outlined"
            startIcon={<Icon sx={{ fontSize: 18 }}>phone</Icon>}
            onClick={handleOpenCallDialog}
            sx={{
              flex: 1,
              height: '36px',
              borderRadius: '8px',
              fontSize: '0.813rem',
              px: 1.5,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              '& .MuiButton-startIcon': {
                marginRight: 0.5,
              },
              background: darkMode 
                ? 'linear-gradient(135deg, rgba(33, 209, 159, 0.1) 0%, rgba(33, 209, 159, 0.05) 100%)'
                : theme.palette.background.glass,
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: theme => theme.palette.text.primary,
              border: '1px solid',
              borderColor: darkMode 
                ? 'rgba(33, 209, 159, 0.2)' 
                : 'rgba(33, 209, 159, 0.15)',
              boxShadow: darkMode
                ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                : '0 2px 8px rgba(33, 209, 159, 0.1)',
              textTransform: 'none',
              fontWeight: 500,
              fontFamily: theme.typography.button.fontFamily,
              letterSpacing: '0.01em',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: darkMode 
                  ? 'linear-gradient(135deg, rgba(33, 209, 159, 0.15) 0%, rgba(33, 209, 159, 0.08) 100%)'
                  : 'linear-gradient(135deg, rgba(33, 209, 159, 0.1) 0%, rgba(33, 209, 159, 0.05) 100%)',
                borderColor: darkMode 
                  ? 'rgba(33, 209, 159, 0.3)' 
                  : 'rgba(33, 209, 159, 0.25)',
                boxShadow: darkMode
                  ? '0 4px 16px rgba(0, 0, 0, 0.4)'
                  : '0 4px 16px rgba(33, 209, 159, 0.2)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Call
            </Box>
          </Button>
        </Box>
      </Box>

      {/* Call Dialog */}
      <ContactCallDialog
        open={callDialogOpen}
        contactId={contactId}
        onClose={handleCloseCallDialog}
      />

      {/* Inline Note Form */}
      {showNoteForm && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '16px',
            background: darkMode
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%)'
              : theme.palette.background.glassStrong,
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: darkMode
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(73, 73, 200, 0.1)',
            boxShadow: darkMode
              ? '0 4px 16px rgba(0, 0, 0, 0.3)'
              : '0 4px 16px rgba(73, 73, 200, 0.08)',
            position: 'relative'
          }}
        >
          {isSubmittingNote && (
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                zIndex: 1
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
          <NoteForm
            initialValues={{
              owner_id: contactId,
              owner_type: 'Contact',
              status: 'active',
              subject: '',
              body: ''
            }}
            onSave={handleSaveNote}
            onCancel={handleCancelNote}
          />
        </Paper>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '200px'
        }}>
          <CircularProgress size={40} />
        </Box>
      )}
      
      {/* Error message */}
      {error && (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center',
          color: 'error.main',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'error.main',
          bgcolor: 'error.light',
          mb: 2
        }}>
          <Typography variant="body1">
            {error}
          </Typography>
        </Box>
      )}
      
      {/* Empty state when no entries exist */}
      {!loading && timelineEntries.length === 0 && !showNoteForm && (
        <EmptyTimelineState 
          onAddNote={handleAddNoteClick} 
          onNewMessage={onNewMessage} 
        />
      )}
      
      {/* Timeline entries */}
      {!loading && (timelineEntries.length > 0 || showNoteForm) && (
        <TimelineContainer>
          {timelineEntries.map((entry, index) => 
            entry.type === 'note' ? (
              <TimelineEntry
                key={`note-${entry.data.id}-${index}`}
                type="note"
                item={entry.data}
                timestamp={entry.timestamp}
                onDelete={handleDeleteNote}
                onUpdate={handleUpdateNote}
              />
            ) : (
              <Box
                key={`conversation-${entry.data.id}-${index}`}
                sx={{ position: 'relative' }}
              >
                <TimelineEntry
                  type="conversation"
                  item={entry.data}
                  timestamp={entry.timestamp}
                  onClick={() => handleConversationClick(entry.data.id, entry.data.channel_id)}
                  onIconMouseEnter={(e) => handleConversationMouseEnter(e, entry.data)}
                  onIconMouseLeave={handleConversationMouseLeave}
                />
              </Box>
            )
          )}
        </TimelineContainer>
      )}
      
      {/* Conversation Preview Tooltip */}
      {tooltipConversationId && tooltipAnchorEl && (
        <ConversationPreviewTooltip 
          conversationId={tooltipConversationId}
          anchorEl={tooltipAnchorEl}
          onClose={handleCloseTooltip}
          open={true}
        />
      )}
    </Box>
  );
};

export default ContactTimeline; 
